import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/customer-memberships';

const FILTER_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'hva', label: 'HVA Var' },
  { key: 'kazandirio', label: 'Kazandırio Var' },
  { key: 'photo', label: 'Fotoğraf Var' },
];

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    total: pick(data, 'total', 'Total') ?? 0,
    withHvaCount: pick(data, 'withHvaCount', 'WithHvaCount') ?? 0,
    withPhotosCount: pick(data, 'withPhotosCount', 'WithPhotosCount') ?? 0,
    withKazandirioCount: pick(data, 'withKazandirioCount', 'WithKazandirioCount') ?? 0,
  };
}

// KazandırioItemsJson → [{productName, quantity}]; bozuk JSON'da boş dizi.
function parseKazandirio(json) {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function parsePhotos(csv) {
  if (!csv) return [];
  return String(csv).split(',').map(u => u.trim()).filter(Boolean);
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    purple: { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.32)', color: '#5b21b6', dot: '#8b5cf6' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

function HvaProgress({ current, target, tier, compact }) {
  const cur = Number(current);
  const tgt = Number(target);
  const hasData = Number.isFinite(cur) && cur >= 0;
  if (!hasData && !tier) return <span style={{ color: '#94a3b8' }}>-</span>;
  const pct = Number.isFinite(tgt) && tgt > 0 ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: compact ? 120 : 200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: compact ? 11 : 12, fontWeight: 600 }}>
        <span style={{ color: '#5b21b6' }}>{tier || 'HVA'}</span>
        <span style={{ color: '#6b7280' }}>
          {Number.isFinite(cur) ? cur : '-'}{Number.isFinite(tgt) && tgt > 0 ? ` / ${tgt}` : ''}
        </span>
      </div>
      <div style={{ height: compact ? 6 : 8, borderRadius: 999, background: 'rgba(139, 92, 246, 0.15)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #8b5cf6, #6366f1)', borderRadius: 999 }} />
      </div>
    </div>
  );
}

export function CustomerMembershipsPageLegacy() {
  const [filterTab, setFilterTab] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, withHvaCount: 0, withPhotosCount: 0, withKazandirioCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        total: data.total,
        withHvaCount: data.withHvaCount,
        withPhotosCount: data.withPhotosCount,
        withKazandirioCount: data.withKazandirioCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      // Filtre sekmesi
      if (filterTab === 'hva') {
        const cur = pick(row, 'hvaCurrentPoints', 'HvaCurrentPoints');
        if (cur == null && !pick(row, 'hvaTierName', 'HvaTierName')) return false;
      } else if (filterTab === 'kazandirio') {
        if (parseKazandirio(pick(row, 'kazandirioItemsJson', 'KazandirioItemsJson')).length === 0) return false;
      } else if (filterTab === 'photo') {
        if (parsePhotos(pick(row, 'frontPhotoUrlsCsv', 'FrontPhotoUrlsCsv')).length === 0) return false;
      }
      // Arama
      if (!needle) return true;
      const fields = [
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'hvaTierName', 'HvaTierName'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'HvaTierName': return pick(row, 'hvaTierName', 'HvaTierName') || '';
        case 'HvaCurrentPoints': return Number(pick(row, 'hvaCurrentPoints', 'HvaCurrentPoints')) || 0;
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filterTab, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'customerTitle',
      headerName: 'Müşteri',
      flex: 1.4,
      minWidth: 200,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '-',
    },
    {
      field: 'hva',
      headerName: 'HVA İlerleme',
      flex: 1.1,
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <HvaProgress
          compact
          current={pick(row, 'hvaCurrentPoints', 'HvaCurrentPoints')}
          target={pick(row, 'hvaTargetPoints', 'HvaTargetPoints')}
          tier={pick(row, 'hvaTierName', 'HvaTierName')}
        />
      ),
    },
    {
      field: 'kazandirio',
      headerName: 'Kazandırio',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      valueGetter: ({ row }) => {
        const n = parseKazandirio(pick(row, 'kazandirioItemsJson', 'KazandirioItemsJson')).length;
        return n > 0 ? `${n} ürün` : '-';
      },
    },
    {
      field: 'photos',
      headerName: 'Fotoğraf',
      flex: 0.5,
      minWidth: 90,
      sortable: false,
      valueGetter: ({ row }) => {
        const n = parsePhotos(pick(row, 'frontPhotoUrlsCsv', 'FrontPhotoUrlsCsv')).length;
        return n > 0 ? n : '-';
      },
    },
    {
      field: 'updatedDate',
      headerName: 'Güncelleme',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'updatedDate', 'UpdatedDate')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => setDetailRow(row)}
          >
            Detay
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  const detailKazandirio = detailRow ? parseKazandirio(pick(detailRow, 'kazandirioItemsJson', 'KazandirioItemsJson')) : [];
  const detailPhotos = detailRow ? parsePhotos(pick(detailRow, 'frontPhotoUrlsCsv', 'FrontPhotoUrlsCsv')) : [];

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Müşteri Üyelik & Puan</h1>
          <p style={s.pageSubtitle}>
            Müşteri başına HVA (Yüksek Değerli Anlaşma) puan ilerlemesi, KazandıRio
            sadakat ürünleri ve tabela fotoğrafları. Mobile delta ile çeker; admin görüntüleme.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Toplam', counts.total, 'gray')}
          {badge('HVA Olan', counts.withHvaCount, 'purple')}
          {badge('Kazandırio', counts.withKazandirioCount, 'green')}
          {badge('Fotoğraflı', counts.withPhotosCount, 'orange')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => { setFilterTab(tab.key); setPage(1); }}
              style={{
                ...(filterTab === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Müşteri / kademe..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={(row) => setDetailRow(row)}
        exportFileName="musteri-uyelik"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Müşteri Üyelik'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>HVA İlerleme</label>
                <HvaProgress
                  current={pick(detailRow, 'hvaCurrentPoints', 'HvaCurrentPoints')}
                  target={pick(detailRow, 'hvaTargetPoints', 'HvaTargetPoints')}
                  tier={pick(detailRow, 'hvaTierName', 'HvaTierName')}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>KazandıRio Ürünleri ({detailKazandirio.length})</label>
                {detailKazandirio.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Kayıt yok</div>
                ) : (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 240, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailKazandirio.map((item, idx) => (
                          <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                              {pick(item, 'productName', 'ProductName') || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(item, 'quantity', 'Quantity') ?? '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Tabela Fotoğrafları ({detailPhotos.length})</label>
                {detailPhotos.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Fotoğraf yok</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {detailPhotos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noreferrer"
                         style={{ display: 'block', width: 120, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                        <img
                          src={url}
                          alt={`Tabela ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Son Güncelleme</label>
                <div style={s.inputView}>{formatDate(pick(detailRow, 'updatedDate', 'UpdatedDate'))}</div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CustomerMembershipsPageLegacy;
